/**
 * Daily prayer times for SLA pauses (optional per tenant). Standard astronomical method, as used by published
 * timetables: the sun's declination and equation of time for the date, solar noon for Dhuhr, a shadow-length
 * rule for Asr, sunset (sun 0.833° below the horizon) for Maghrib, and a twilight angle, or a fixed interval
 * after Maghrib, for Isha.
 *
 * Only the prayers that can fall inside business hours matter here (Dhuhr, Asr, Maghrib, Isha). Results are
 * within a minute or two of official timetables, which add their own small safety margins. Unit tests pin
 * reference values, and the settings screen shows today's computed times so a tenant can check them against
 * their local authority's timetable.
 */
export type PrayerMethod = 'umm_al_qura' | 'uae' | 'mwl' | 'karachi' | 'kuwait' | 'qatar';
export type AsrRule = 'standard' | 'hanafi';
export type PrayerName = 'dhuhr' | 'asr' | 'maghrib' | 'isha';

export const PRAYER_METHODS: Record<
  PrayerMethod,
  { isha: { angle: number } | { minutesAfterMaghrib: number; ramadanMinutes?: number }; label: string }
> = {
  // Umm al-Qura University, Makkah (KSA official): Isha 90 minutes after Maghrib, 120 in Ramadan.
  umm_al_qura: {
    isha: { minutesAfterMaghrib: 90, ramadanMinutes: 120 },
    label: 'Umm al-Qura (Saudi Arabia)',
  },
  // UAE General Authority of Islamic Affairs and Endowments: Isha at 18.2°.
  uae: { isha: { angle: 18.2 }, label: 'UAE (Awqaf)' },
  mwl: { isha: { angle: 17 }, label: 'Muslim World League' },
  // University of Islamic Sciences, Karachi (common in South Asia).
  karachi: { isha: { angle: 18 }, label: 'Karachi' },
  kuwait: { isha: { angle: 17.5 }, label: 'Kuwait' },
  qatar: { isha: { minutesAfterMaghrib: 90 }, label: 'Qatar' },
};

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const fix = (a: number, b: number) => ((a % b) + b) % b;

/** Sun declination (degrees) and equation of time (hours) for a Julian date. */
function sunPosition(jd: number) {
  const d = jd - 2451545.0;
  const g = fix(357.529 + 0.98560028 * d, 360);
  const q = fix(280.459 + 0.98564736 * d, 360);
  const l = fix(q + 1.915 * Math.sin(rad(g)) + 0.02 * Math.sin(rad(2 * g)), 360);
  const e = 23.439 - 0.00000036 * d;
  const ra = fix(deg(Math.atan2(Math.cos(rad(e)) * Math.sin(rad(l)), Math.cos(rad(l)))) / 15, 24);
  return { declination: deg(Math.asin(Math.sin(rad(e)) * Math.sin(rad(l)))), equation: q / 15 - ra };
}

function julianDate(y: number, m: number, d: number) {
  if (m <= 2) {
    y -= 1;
    m += 12;
  }
  const a = Math.floor(y / 100);
  const b = 2 - a + Math.floor(a / 4);
  return Math.floor(365.25 * (y + 4716)) + Math.floor(30.6001 * (m + 1)) + d + b - 1524.5;
}

/**
 * Prayer times for a local date "YYYY-MM-DD" at a location, as UTC instants. Each time is refined twice by
 * recomputing the sun's position at the approximate moment (as the reference implementations do).
 */
export function prayerTimes(
  date: string,
  place: { latitude: number; longitude: number },
  method: PrayerMethod = 'mwl',
  asr: AsrRule = 'standard',
  opts: { ramadan?: boolean } = {},
): Record<PrayerName, Date> {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number];
  const jd0 = julianDate(y, m, d) - place.longitude / 360;
  const lat = place.latitude;

  // Hours are local mean solar time (as in the reference algorithm); converted to UTC once at the end.
  const noon = (h: number) => 12 - sunPosition(jd0 + h / 24).equation;
  const angleTime = (angle: number, h: number) => {
    const { declination } = sunPosition(jd0 + h / 24);
    const cos =
      (-Math.sin(rad(angle)) - Math.sin(rad(declination)) * Math.sin(rad(lat))) /
      (Math.cos(rad(declination)) * Math.cos(rad(lat)));
    return noon(h) + deg(Math.acos(Math.min(1, Math.max(-1, cos)))) / 15;
  };
  const asrTime = (h: number) => {
    const { declination } = sunPosition(jd0 + h / 24);
    const factor = asr === 'hanafi' ? 2 : 1;
    const angle = -deg(Math.atan(1 / (factor + Math.tan(rad(Math.abs(lat - declination))))));
    return angleTime(angle, h);
  };
  const refine = (f: (h: number) => number, guess: number) => f(f(guess));

  const dhuhr = refine(noon, 12);
  const asrH = refine(asrTime, 15);
  const maghrib = refine((h) => angleTime(0.833, h), 18);
  const rule = PRAYER_METHODS[method].isha;
  const isha =
    'angle' in rule
      ? refine((h) => angleTime(rule.angle, h), 19.5)
      : maghrib + ((opts.ramadan && rule.ramadanMinutes) || rule.minutesAfterMaghrib) / 60;

  // Officially published times are rounded to whole minutes; round to the nearest.
  const at = (solar: number) =>
    new Date(
      Math.round((Date.UTC(y, m - 1, d) + (solar - place.longitude / 15) * 3_600_000) / 60_000) * 60_000,
    );
  return { dhuhr: at(dhuhr), asr: at(asrH), maghrib: at(maghrib), isha: at(isha) };
}

/** Reasonable defaults per country (the capital); tenants should set their own city for accuracy. */
export const DEFAULT_PRAYER_PLACE: Record<
  string,
  { latitude: number; longitude: number; method: PrayerMethod; asr: AsrRule }
> = {
  AE: { latitude: 25.2048, longitude: 55.2708, method: 'uae', asr: 'standard' }, // Dubai
  SA: { latitude: 24.7136, longitude: 46.6753, method: 'umm_al_qura', asr: 'standard' }, // Riyadh
  QA: { latitude: 25.2854, longitude: 51.531, method: 'qatar', asr: 'standard' }, // Doha
  KW: { latitude: 29.3759, longitude: 47.9774, method: 'kuwait', asr: 'standard' }, // Kuwait City
  BH: { latitude: 26.2285, longitude: 50.586, method: 'umm_al_qura', asr: 'standard' }, // Manama
  OM: { latitude: 23.588, longitude: 58.3829, method: 'mwl', asr: 'standard' }, // Muscat
  IN: { latitude: 19.076, longitude: 72.8777, method: 'karachi', asr: 'hanafi' }, // Mumbai
};
